/**
 * The pay-card proxy id's format rule, as much of it as this app can legitimately hold [decision 0181;
 * `tasks/_shared/contracts.md` § B], and the by-hand field's mask [decision 0423; 03-us-kpc-1e].
 *
 * ISS's payroll audit enforces two rules and no third — *"if it's something different than that, it'll kick
 * out."* — and OQ-89 (how many trailing digits vary, whether there is a checksum) is open, so nothing
 * stricter may be derived here:
 *
 * 1. **Exactly 16 digits.** Below.
 * 2. **It begins with the deployment's configured prefix.** Held by the api, never by a literal here.
 *
 * ## Where the prefix comes from
 *
 * The api holds the prefix as configuration (`KIOSK_PAYCARD_PROXY_ID_PREFIX`) precisely because 0181 was
 * raised about it being a literal. Since 2026-10-05 the app READS it from the api, behind the kiosk
 * credential (`services/payCardPrefix.ts`) — the Operator's answer to Q6 in 0423, which closed OQ-151. It
 * is still never a literal in a component nor a `VITE_` value inlined into JavaScript a browser downloads.
 *
 * **The mask fixes the prefix; it adds no rule** [0423: *"The mask adds no validation rule"*]. When the read
 * answers, the field shows the prefix and the candidate types only the positions after it, so a typed value
 * can no longer carry a wrong one. When it does not answer, the field is today's plain entry and a 16-digit
 * id with the wrong prefix still reaches the api and comes back `rejected`.
 *
 * Client validation never replaces the server's: `pay-card.dto.ts` and `ProxyIdFormatPipe` must keep
 * refusing a bad id whatever this file says.
 */

/** 16. The api's `PAY_CARD_PROXY_ID_LENGTH`, which is the same number for the same reason. */
export const PAY_CARD_PROXY_ID_LENGTH = 16;

/**
 * Digits, and exactly 16 of them.
 *
 * ⚠️ **Digits, not characters.** Decision 0181 says *"16 characters"*; the Operator confirmed digits-only on
 * 2026-09-10 and that 0181's wording is what is wrong. The api's `@Matches` is anchored `[0-9]{16}` and the
 * two sides may not disagree — a client that accepted a letter would send what the api refuses, which is
 * the exact drift § B names.
 */
const PAY_CARD_PROXY_ID_SHAPE = new RegExp(`^[0-9]{${String(PAY_CARD_PROXY_ID_LENGTH)}}$`);

/**
 * Whether a value has the shape this app can check — 16 digits.
 *
 * **A `true` is not a promise the api will accept it**, and no caller may treat it as one: the prefix rule
 * lives on the api and is not checked here. It answers one question only: is this worth sending?
 */
export function isPayCardProxyIdShape(value: string): boolean {
  return PAY_CARD_PROXY_ID_SHAPE.test(value);
}

/**
 * A decoded read with its spaces and hyphens dropped — separators are display only [03-us-kpc-1e AC-6].
 *
 * One function so the layer that decides whether a read is OFFERED [KPC-1c] and the screen that ENTERS it
 * [KPC-1e] judge the same string.
 */
export function withoutSeparators(read: string): string {
  return read.replace(/[\s-]/g, '');
}

/**
 * Whether a camera read may be OFFERED for confirmation [KPC-1c] and ENTERED into the fields [03-us-kpc-1e].
 *
 * Only exactly 16 digits that begin with the prefix. Anything else — a 12-digit UPC-A off the packet
 * included — is a FAILED READ: never offered, never entered, and **never completed with the prefix**: a
 * short read is a misread, and padding it would invent the digits the camera did not see.
 *
 * An empty prefix is the fallback form (the prefix read did not answer): then the app checks the half it
 * can, the 16 digits, and the api keeps the prefix as it did before the mask.
 */
export function isEnterableRead(read: string, prefix: string): boolean {
  return isPayCardProxyIdShape(read) && read.startsWith(prefix);
}

/**
 * What a paste or a drop does to a field that accepts one [03-us-kpc-1e AC-6; eval-kpc-1e E6 runs 5–6].
 *
 * - `whole` — only digits, more than the editable positions, at most 16, beginning with the prefix: the
 *   prefix takes its fixed place and `digits` (the rest) fill the editable positions, as for a read.
 * - `asTyped` — only digits, no more than the editable positions: they fill them as typed digits would.
 * - `refused` — anything else (another prefix, more than 16 digits, any other character): nothing is
 *   entered, and the format message shows. ⛔ Never reshaped so that it fits.
 *
 * Spaces and hyphens are ignored first, because separators are display only.
 */
export type PastedEntry =
  | { readonly kind: 'whole'; readonly digits: string }
  | { readonly kind: 'asTyped'; readonly digits: string }
  | { readonly kind: 'refused' };

export function pastedEntry(text: string, prefix: string): PastedEntry {
  const cleaned = text.replace(/[\s-]/g, '');
  if (!/^[0-9]+$/.test(cleaned)) return { kind: 'refused' };
  if (cleaned.length <= editablePositions(prefix)) return { kind: 'asTyped', digits: cleaned };
  if (cleaned.length <= PAY_CARD_PROXY_ID_LENGTH && cleaned.startsWith(prefix)) {
    return { kind: 'whole', digits: cleaned.slice(prefix.length) };
  }
  return { kind: 'refused' };
}

/**
 * What separates the groups of four — **display only** [0423: *"the separators are display only"*].
 *
 * The ruling's own example, `7090 - XXXX - XXXX - XXXX`; no frame draws the mask, so the example is the
 * only visual reference until Design draws one [0423 § *Design, not a blocker*]. Not a sentence and not
 * translated: it is the same mark in English and Spanish.
 */
export const PROXY_ID_GROUP_SEPARATOR = ' - ';

const GROUP_LENGTH = 4;

/** How many positions the candidate types after the prefix: 16 − p [0423; no count of its own, OQ-89]. */
export function editablePositions(prefix: string): number {
  return Math.max(0, PAY_CARD_PROXY_ID_LENGTH - prefix.length);
}

/**
 * The digits a masked field holds, from whatever the input now shows: digits only, in order, capped at the
 * editable positions. A letter or a symbol is not entered [0423, Q4: *"Solo numeros"*].
 *
 * ⚠️ **Typed digits fill the editable positions, whatever they are** — including the prefix typed again.
 * That is 0423 as written; what should happen instead is open decision 0459, and no builder chooses it.
 */
export function toEditableDigits(shown: string, prefix: string): string {
  return shown.replace(/\D/g, '').slice(0, editablePositions(prefix));
}

/**
 * The fixed half of the mask, as shown: the prefix in its groups of four, and the separator after it when
 * the prefix ends a group. `1029` → `1029 - `; `102` → `102` (the group goes on in the editable half).
 */
export function maskedPrefix(prefix: string): string {
  let shown = '';
  for (let position = 0; position < prefix.length; position += 1) {
    if (position > 0 && position % GROUP_LENGTH === 0) shown += PROXY_ID_GROUP_SEPARATOR;
    shown += prefix.charAt(position);
  }
  const endsAGroup =
    prefix.length > 0 &&
    prefix.length % GROUP_LENGTH === 0 &&
    prefix.length < PAY_CARD_PROXY_ID_LENGTH;
  return endsAGroup ? shown + PROXY_ID_GROUP_SEPARATOR : shown;
}

/**
 * The editable half, as shown: the digits grouped by their position in the WHOLE id, not by their own —
 * the groups of four follow the position, as the ruling's example does [0423]. With a three-digit prefix
 * the first group has one editable digit.
 */
export function maskedDigits(prefix: string, digits: string): string {
  let shown = '';
  for (let index = 0; index < digits.length; index += 1) {
    if (index > 0 && (prefix.length + index) % GROUP_LENGTH === 0)
      shown += PROXY_ID_GROUP_SEPARATOR;
    shown += digits.charAt(index);
  }
  return shown;
}
